import { DeviceLicence } from './device-licence';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';

export class Device {
  public type: DeviceType;

  public id: string;
  public name: string;
  /**
   * Child Control version concatenated with OS version ('ccVersion|osVersion')
   */
  public versionInfo: string;
  /**
   * Timestamp (as string) of lastHistory (=last sync datetime)
   */
  public lastHistory: string;
  /**
   * Last user (if Child Control type CC)
   */
  public lastUser: string;
  /**
   * Only available with Android devices
   */
  public manufacturer?: string;

  /**
   * Only available with Windows devices
   */
  public users?: string[];

  /**
   * Child Control version
   */
  public get version(): string {
    const pipeIndex = this.versionInfo.indexOf('|');
    return this.versionInfo.slice(0, pipeIndex);
  }

  /**
   * OS Version (Android or Windows)
   */
  public get osVersion(): string {
    const pipeIndex = this.versionInfo.indexOf('|');
    const osVersionString = this.versionInfo.slice(pipeIndex + 1, this.versionInfo.length);

    if (this.type === DeviceType.CB) {
      // Remove "Android " from version string (e.g. Android 10.0 => 10.0)
      return osVersionString.slice(8, osVersionString.length);
    } else {
      return osVersionString;
    }
  }

  public get needSync(): boolean {
    return this.needSyncChangeSubject.value.needSync;
  }
  /** Emits when needSync changes. Also emits the current needSync value on subscribing. */
  public get needSyncChange(): Observable<{ needSync: boolean, localCause: boolean }> {
    return this.needSyncChangeSubject
    .asObservable()
    .pipe(distinctUntilChanged((x, y) => x.needSync === y.needSync && x.localCause === y.localCause));
  }
  private needSyncChangeSubject = new BehaviorSubject<NeedSyncChangeData>({ needSync: false, localCause: true});

  /**
   * Emits when sync is finished (i.e. when needSync changes from true to false).
   * 
   * This does not necessarily mean that the sync was successful.
   * It only means, that all changes are in sync with the device after a sync was triggered by setting needSync to true.
   */
  public get syncFinished(): Observable<void> {
    return this.needSyncChange.pipe(
      // Ignore current value emitted on subscribing
      skip(1),
      // Assume sync is finished when needSync switches back to false
      filter(syncChange => syncChange.needSync === false),
      map(() => undefined)
    );
  }

  constructor(apiDevice?: ApiDevice) {
    if (!apiDevice) {
      return;
    }

    // Manually assign device type to force that new types to be manually handled here.
    // If the device type is not handled, this.type will be undefined and cause errors.
    switch (apiDevice.deviceType) {
      case 1:
        this.type = DeviceType.CC;
        break;
      case 30:
        this.type = DeviceType.CB;
        break;
      case 90:
        this.type = DeviceType.Invalid;
        break;
    }

    this.id = apiDevice.deviceId;
    this.name = apiDevice.deviceName;
    this.versionInfo = apiDevice.versionInfo;

    this.lastHistory = apiDevice.lastHistory;
    this.lastUser = apiDevice.lastUser;

    if (apiDevice.manufacturer != null) {
      this.manufacturer = apiDevice.manufacturer;
    }


    if (apiDevice.users && apiDevice.users.length > 0) {
      this.users = [];
      apiDevice.users.forEach(user => {
        this.users.push(user.username);
      });
    }
  }

  /** Sets the needSync property. Set `localCause = false` if the needSync change is not induced by a local settings change. */
  public setNeedSync(value: boolean, localCause = true) {
    this.needSyncChangeSubject.next({ needSync: value, localCause });
  }
}

/**
 * Device object as it is received from the API
 */
export interface ApiDevice {
  deviceId: string;
  deviceName: string;
  deviceType: number;
  versionInfo: string;
  lastHistory: string;
  lastUser: string;
  manufacturer?: string;
  users?: [
    {
      username: string
    }
  ];
}

export class ActiveDevice {
  private _licence?: DeviceLicence;

  public device: Device;
  /**
   * Undefined if the active device does not support users
   */
  public username?: string;

  /**
   * Returns the licence of the device.
   *
   * **IMPORTANT**: The licence can be undefined
   *
   * The licence can be undefined because it is lazy loaded.
   * Use the onLincenceChange Observable to get notified as soon as the licence is known.
   */
  public get licence(): DeviceLicence|undefined {
    return this._licence;
  }

  public set licence(value: DeviceLicence) {
    this._licence = value;
    this.onLincenceChangeSubject.next(this._licence);
  }

  private onLincenceChangeSubject = new ReplaySubject<DeviceLicence>(1);

  /**
   * Can be subscribed to get the licence as soon as it is loaded.
   *
   * Emits the licence on subscription if it's already known.
   */
  public get onLincenceChange(): Observable<DeviceLicence> {
    return this.onLincenceChangeSubject.asObservable();
  }

  constructor(device: Device, username?: string, licence?: DeviceLicence) {
    this.device = device;
    this.username = username;
    this._licence = licence;
  }
}

export enum DeviceType {
  /**
   * Windows Device
   */
  CC = 1,
  /**
   * Android Device
   */
  CB = 30,
  /**
   * Defektes/zu prüfendes Gerät — Backend liefert deviceType 90,
   * wenn das Gerät neu geprüft oder ggf. neu installiert werden soll.
   */
  Invalid = 90
}

export type NeedSyncChangeData = {
  /** New needSyncValue */
  needSync: boolean,
  /**
   * Whether the change was caused by a local settings change (true)
   * or by old unsynced changes which are not from the current context (false).
   * A non-local cause is usually just informational and does not require the observation of the needSync status via repeating API calls.
   */
  localCause: boolean
};
